/* assets/video-autoplay.js
   - PDP: Autoplay/pause any <video> in gallery, content, or sticky bar.
   - Collections: If a product card has no <video>, fetch /products/{handle}.js,
     inject the first MP4 product video, and autoplay/pause it like RL.
   - Safe for Prestige re-renders (<product-rerender>) and sliders.
*/

(function () {
  /*** CONFIG ***/
  // Max cards to upgrade per page to avoid excessive fetches
  const MAX_CARDS_TO_UPGRADE = 20;

  // Selectors where videos may already exist (PDP + cards)
  const VIDEO_SELECTORS = [
    '.product-gallery video',
    '.product-content-below-gallery video',
    '.product-sticky-bar video',
    '.card__media video',
    '.product-card video',
    '[data-product-card] video',
    '.card-media video',
    '.media video'
  ].join(',');

  // Likely media containers on cards (we’ll inject <video> here)
  const CARD_MEDIA_SELECTORS = [
    '.card__media',
    '.product-card__media',
    '.card-media',
    '.media',
    '[data-product-media]'
  ];

  /************** Utilities **************/
  const prepared = new WeakSet();
  const injected = new WeakSet();
  const productCache = new Map(); // handle -> product JSON
  let cardsUpgraded = 0;

  function inViewportPlayPolicy(entry) {
    return entry.isIntersecting && entry.intersectionRatio >= 0.5;
  }

  function prepareVideo(video) {
    if (!video || prepared.has(video)) return;
    video.muted = true; video.setAttribute('muted', '');
    video.playsInline = true; video.setAttribute('playsinline', '');
    video.loop = true; video.setAttribute('loop', '');
    // Start fast, stay light:
    if (!video.getAttribute('preload')) video.setAttribute('preload', 'metadata');
    // Hide default controls for RL-style motion
    video.removeAttribute('controls');
    // Accessibility: show controls when focused via keyboard
    video.addEventListener('focus', () => video.setAttribute('controls', ''));
    video.addEventListener('blur',  () => video.removeAttribute('controls'));
    prepared.add(video);
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const v = entry.target;
      prepareVideo(v);
      if (inViewportPlayPolicy(entry)) {
        v.play().catch(() => {});
      } else {
        try { v.pause(); } catch (_) {}
      }
    });
  }, { threshold: [0, 0.5, 1] });

  function watchExistingVideos(root = document) {
    root.querySelectorAll(VIDEO_SELECTORS).forEach((v) => {
      prepareVideo(v);
      io.observe(v);
    });
  }

  // Extract product handle from a URL like /products/handle or /products/handle?x=y
  function handleFromHref(href) {
    try {
      const u = new URL(href, location.origin);
      const parts = u.pathname.split('/products/');
      if (parts.length < 2) return null;
      // If a locale or /collections/ is in path, we still split on /products/
      const tail = parts[1]; // e.g. "my-shoe" or "my-shoe/some"
      const h = tail.split('/')[0]; // "my-shoe"
      return h || null;
    } catch (_) { return null; }
  }

  function findCardMediaContainer(card) {
    for (const sel of CARD_MEDIA_SELECTORS) {
      const el = card.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  async function fetchProductJSON(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error(`fetch ${handle} ${res.status}`);
      const data = await res.json();
      productCache.set(handle, data);
      return data;
    } catch (_) {
      productCache.set(handle, null);
      return null;
    }
  }

  // Find the first mp4 video from product JSON media array
  function findFirstMp4(product) {
    if (!product || !Array.isArray(product.media)) return null;
    for (const m of product.media) {
      // Shopify returns media_type: 'video' or 'external_video'
      if (m.media_type === 'video' && Array.isArray(m.sources)) {
        const mp4 = m.sources.find(s => (s.mime_type || '').includes('mp4') && s.url);
        if (mp4) return mp4.url;
      }
    }
    return null;
  }

  function createCardVideo(src) {
    const v = document.createElement('video');
    v.className = 'product-card-video';
    v.src = src;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('loop', '');
    v.setAttribute('preload', 'metadata');
    // No controls for motion-preview cards
    return v;
  }

  function cardAlreadyHasVideo(card) {
    return !!card.querySelector('video');
  }

  function findCardLink(card) {
    // Prefer link around media; fall back to first product link in card
    return (
      card.querySelector('a[href*="/products/"]') ||
      card.closest('a[href*="/products/"]')
    );
  }

  async function upgradeCard(card) {
    if (injected.has(card) || cardAlreadyHasVideo(card)) return;
    if (cardsUpgraded >= MAX_CARDS_TO_UPGRADE) return;

    const a = findCardLink(card);
    const handle = a ? handleFromHref(a.getAttribute('href')) : null;
    if (!handle) return;

    const product = await fetchProductJSON(handle);
    const mp4 = findFirstMp4(product);
    if (!mp4) return;

    const container = findCardMediaContainer(card) || card;
    const img = container.querySelector('img');

    const vid = createCardVideo(mp4);
    // Size/cover
    vid.style.width = '100%';
    vid.style.height = '100%';
    vid.style.objectFit = 'cover';
    vid.style.display = 'block';
    vid.setAttribute('aria-hidden', 'true');

    // Insert video before the image (keeps the image as fallback)
    if (img && img.parentNode === container) {
      container.insertBefore(vid, img);
    } else {
      container.prepend(vid);
    }

    // Observe for autoplay/pause
    prepareVideo(vid);
    io.observe(vid);

    injected.add(card);
    cardsUpgraded++;
  }

  function collectProductCards(root = document) {
    // General heuristics for product cards on collections
    const cards = Array.from(root.querySelectorAll(
      '.card, .product-card, [data-product-card], li.grid__item, .collection-product, .product-item'
    ));
    return cards.filter(Boolean);
  }

  async function upgradeVisibleCards(root = document) {
    const cards = collectProductCards(root);
    for (const card of cards) {
      // Don’t block main thread; schedule upgrades
      // eslint-disable-next-line no-await-in-loop
      await upgradeCard(card);
      if (cardsUpgraded >= MAX_CARDS_TO_UPGRADE) break;
    }
  }

  /*** Boot ***/
  function boot(root = document) {
    watchExistingVideos(root);     // Autoplay any existing videos
    upgradeVisibleCards(root);     // Inject video into cards that only have images
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  // Respond to dynamic changes in Prestige (re-render, variant, sliders, tabs, popovers)
  document.addEventListener('product:media:changed', () => boot());
  document.addEventListener('variant:changed', () => boot());

  // MutationObserver to catch lazy-loaded cards or infinite scroll
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('video')) {
          prepareVideo(node); io.observe(node);
        } else {
          boot(node);
        }
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
